import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FAQSection from '@/components/base/FAQSection';
import { aboutFAQList } from '@/mocks/faq';

export default function About() {
  const organizationLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黄油游戏下载',
    url: 'https://www.huangyougame.com/',
    description: '专注于为中文玩家提供安全、可靠游戏下载指南的平台',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@huangyougame.com',
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
  };

  const webpageLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '关于我们 - 黄油游戏下载',
    description: '了解黄油游戏下载的品牌故事、口碑评价与联系方式',
    url: 'https://www.huangyougame.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '黄油游戏下载',
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.huangyougame.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
        item: 'https://www.huangyougame.com/about',
      },
    ],
  };

  const milestones = [
    { year: '2024年3月', title: '平台创立', desc: '黄油游戏下载平台正式上线，致力于为中文玩家提供高质量的游戏下载教程。' },
    { year: '2024年9月', title: '内容扩展', desc: '教程内容覆盖六大游戏分类，入驻作者超过20位，月活跃用户突破10万。' },
    { year: '2025年1月', title: '社区建立', desc: '建立玩家交流社区，推出互动评论和文章分享功能，增强玩家之间的交流。' },
    { year: '2025年8月', title: '质量升级', desc: '全面升级内容审核标准，引入游戏行业专家顾问团队，确保每篇教程的专业性。' },
    { year: '2026年3月', title: '生态完善', desc: '推出游戏推荐系统，与多家游戏平台建立合作关系，为用户提供更全面的服务。' },
  ];

  const testimonials = [
    { name: '游戏玩家小王', text: '黄油游戏下载的教程真的很详细！作为新手，我按照教程一步步操作，顺利下载安装了第一游戏。感谢这个平台！', rating: 5 },
    { name: '资深玩家老李', text: '内容专业度很高，每篇教程都经过实际验证。推荐的安全下载渠道确实靠谱，再也不用担心下载到病毒了。', rating: 5 },
    { name: '学生党小张', text: '免费教程质量这么高真的很良心！分类清晰，搜索方便，是我每次下载新游戏前必看的指南网站。', rating: 5 },
    { name: '上班族阿明', text: 'FAQ板块做得特别好，很多常见问题都能直接找到答案。即使不太懂电脑的人也能跟着教程完成操作。', rating: 4 },
  ];

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify(organizationLd)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(webpageLd)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbLd)}
      </script>

      <Navbar />

      <main className="pt-16">
        <section className="bg-background-100 py-8 md:py-10">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="mt-4">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
                关于我们
              </h1>
              <p className="text-sm text-foreground-600 max-w-2xl">
                了解黄油游戏下载的故事与使命
              </p>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-4">
                  我们的故事
                </h2>
                <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    黄油游戏下载诞生于<strong>一个简单的想法</strong>——让每一位中文玩家都能安全、轻松地下载和安装自己喜欢的游戏。
                  </p>
                  <p>
                    在游戏行业高速发展的今天，越来越多的优秀作品涌现，但游戏下载过程中的各种问题却常常让玩家头疼不已：复杂的安装步骤、难以辨别真伪的下载渠道、层出不穷的安全隐患……这些都是我们致力于解决的问题。
                  </p>
                  <p>
                    我们的团队由一群热爱游戏的资深玩家和技术专家组成。我们深知玩家在下载游戏时的困惑与顾虑，因此创建了黄油游戏下载这个平台——一个专注于<strong>游戏下载教程</strong>的内容网站。每一篇教程都经过团队成员的亲自验证，确保内容的准确性和安全性。
                  </p>
                  <p>
                    从最初的几篇入门教程，到现在涵盖六大游戏分类、上百篇精品文章的知识库，我们始终秉持着<strong>专业、安全、免费</strong>的初心。未来，我们将继续扩展内容版图，为玩家提供更全面的游戏下载指导服务。
                  </p>
                </div>
              </div>
              <div className="relative w-full h-[350px] md:h-[400px] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20cozy%20gaming%20team%20workspace%20illustration%20with%20developers%20and%20players%20collaborating%2C%20orange%20and%20amber%20ambient%20lighting%2C%20modern%20digital%20art%20style%2C%20clean%20composition&width=1200&height=800&seq=about-story-2026&orientation=landscape"
                  alt="黄油游戏下载团队"
                  title="黄油游戏下载 - 我们的故事"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-3">
                发展历程
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                从创立至今，我们一直在成长
              </p>
            </div>
            <div className="relative max-w-3xl mx-auto">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((item, index) => (
                  <div
                    key={index}
                    className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="md:w-1/2 flex items-center gap-4 pl-10 md:pl-0">
                      <div className="md:hidden absolute left-1 w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold z-10">
                        {index + 1}
                      </div>
                      <div className={`${index % 2 === 0 ? 'md:text-right md:pr-8' : 'md:text-left md:pl-8'} flex-1`}>
                        <span className="inline-block px-2.5 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full mb-2 whitespace-nowrap">
                          {item.year}
                        </span>
                        <h3 className="text-base font-semibold text-foreground-900 mb-1">
                          {item.title}
                        </h3>
                        <p className="text-sm text-foreground-600">{item.desc}</p>
                      </div>
                    </div>
                    <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-8 h-8 items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold z-10">
                      {index + 1}
                    </div>
                    <div className="md:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-3">
                玩家口碑
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                听听玩家们怎么说
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.map((item, index) => (
                <div
                  key={index}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-5"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${
                          i < item.rating
                            ? 'ri-star-fill text-accent-500'
                            : 'ri-star-line text-foreground-300'
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{item.text}&rdquo;
                  </p>
                  <p className="text-xs font-semibold text-foreground-900">
                    — {item.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-4">
                  联系我们
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                      <p className="text-sm text-foreground-600">
                        contact@huangyougame.com
                      </p>
                      <p className="text-xs text-foreground-500 mt-1">
                        我们会在24小时内回复你的咨询
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 flex-shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h3>
                      <p className="text-sm text-foreground-600">
                        搜索关注「黄油游戏下载」
                      </p>
                      <p className="text-xs text-foreground-500 mt-1">
                        获取最新游戏资讯和教程推送
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 flex-shrink-0">
                      <i className="ri-bilibili-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">B站频道</h3>
                      <p className="text-sm text-foreground-600">
                        搜索「黄油游戏下载官方」
                      </p>
                      <p className="text-xs text-foreground-500 mt-1">
                        视频教程更直观，欢迎关注
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-4">
                  关于我们的常见问题
                </h2>
                <FAQSection items={aboutFAQList} />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-16 bg-primary-500">
          <div className="w-full px-4 md:px-6 max-w-2xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-heading text-background-50 mb-3">
              加入我们的社区
            </h2>
            <p className="text-sm text-background-50/80 mb-8">
              与数万名玩家一起探索游戏的乐趣，获取最新的下载教程和游戏资讯
            </p>
            <Link
              to="/category/action"
              className="inline-flex px-8 py-3 bg-background-50 text-primary-500 rounded-full text-sm font-semibold hover:bg-background-100 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              开始浏览教程
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}